import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  CheckCircleIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  DocumentTextIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import CustomerCreateModal from '../../components/CustomerCreateModal';
import { FormField, FormSelect, FormTextarea, RequiredFieldLabel } from '../../components/forms';
import { dateToUTCNoon } from '../../utils/dateHelpers';
import {
  AWS_ALL_REGION_CODES,
  AWS_REGION_OPTIONS,
  AWS_SELECT_ALL_REGIONS_VALUE
} from '../../constants/awsRegions';
import { normalizeAwsDetailsForApi } from '../../utils/engagementAwsDetails';
import {
  GCP_REGION_OPTIONS,
  GCP_SELECT_ALL_REGIONS_VALUE,
  type GcpRegionOption
} from './gcpRegions';
import { normalizeGcpDetailsForApi } from './engagementGcpDetails';
import PatchingServicesSection, {
  defaultPatchingServicesFormState,
  patchingServicesToApiPayload,
  PatchingServicesFormState,
  validatePatchingServicesForm
} from '../../components/engagements/PatchingServicesSection';

interface Customer {
  id: string;
  accountName: string;
  accountShortName: string;
}

interface Group {
  id: string;
  name: string;
  description: string;
  memberCount: number;
}

interface GroupMember {
  id: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface EngagementFormData {
  // Step 1
  customerId: string;
  
  // Step 2
  name: string;
  cloudProvider: string;
  description: string;
  billingType: string;
  currency: string;
  startDate: string;
  endDate: string;
  totalBudget: number | null;
  contractValue: number | null;
  minimumServiceFee: number;
  platformCost: number;
  billable: boolean;
  isServiceCreditEngagement: boolean;
  complimentaryHours: number | null;

  // Step 3 - Cloud Provider Details
  azureDetails: {
    tenantId: string;
    clientId: string;
    clientSecret: string;
    subscriptionId: string;
    authorityUrl: string;
  };
  awsDetails: {
    accountId: string;
    region: string;
    accessKeyId: string;
    secretAccessKey: string;
    vpcId: string;
    iamRole: string;
  };
  gcpDetails: {
    projectId: string;
    region: string;
    serviceAccount: string;
    zone: string;
  };
  
  // Step 4
  selectedGroupIds: string[];
  
  // Step 5
  agentRates: {
    userId: string;
    role: string;
    hourlyRate: number;
    allocatedHours: number | null;
  }[];
  
  // Step 6
  selectedPlanIds: string[];
}

const EngagementCreate: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [patchingServices, setPatchingServices] = useState<PatchingServicesFormState>(
    defaultPatchingServicesFormState()
  );
  const navigate = useNavigate();

  const [formData, setFormData] = useState<EngagementFormData>({
    customerId: '',
    name: '',
    cloudProvider: '',
    description: '',
    billingType: 'HOURLY',
    currency: 'USD',
    startDate: '',
    endDate: '',
    totalBudget: null,
    contractValue: null,
    minimumServiceFee: 0,
    platformCost: 0,
    billable: true,
    isServiceCreditEngagement: false,
    complimentaryHours: null,
    azureDetails: {
      tenantId: '',
      clientId: '',
      clientSecret: '',
      subscriptionId: '',
      authorityUrl: ''
    },
    awsDetails: {
      accountId: '',
      region: '',
      accessKeyId: '',
      secretAccessKey: '',
      vpcId: '',
      iamRole: ''
    },
    gcpDetails: {
      projectId: '',
      region: '',
      serviceAccount: '',
      zone: ''
    },
    selectedGroupIds: [],
    agentRates: [],
    selectedPlanIds: []
  });

  const [dateError, setDateError] = useState<string>('');
  const [step3FieldErrors, setStep3FieldErrors] = useState<Record<string, string>>({});

  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('engagements', 'create');

  useEffect(() => {
    if (permissionsLoading) {
      return;
    }
    if (!hasPermission) {
      navigate('/engagements');
      return;
    }
    fetchCustomers();
    fetchGroups();
  }, [hasPermission, permissionsLoading, navigate]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers?limit=1000');
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to fetch customers');
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups/assignment-groups');
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Failed to fetch groups');
    }
  };

  const fetchGroupMembers = async (groupIds: string[]) => {
    if (groupIds.length === 0) {
      setGroupMembers([]);
      setFormData(prev => ({ ...prev, agentRates: [] }));
      return;
    }

    try {
      setLoading(true);
      const memberPromises = groupIds.map(async (groupId) => {
        const response = await api.get(`/groups/${groupId}`);
        return response.data.members || [];
      });

      const allMembers = await Promise.all(memberPromises);
      const flatMembers = allMembers.flat();
      
      // Remove duplicates by userId
      const uniqueMembers = flatMembers.filter((member, index, self) => 
        index === self.findIndex(m => m.userId === member.userId)
      );

      setGroupMembers(uniqueMembers);

      // Initialize agent rates for all members
      const initialRates = uniqueMembers.map(member => ({
        userId: member.userId,
        role: 'DEVELOPER',
        hourlyRate: 100,
        allocatedHours: null
      }));

      setFormData(prev => ({ ...prev, agentRates: initialRates }));
    } catch (error) {
      console.error('Error fetching group members:', error);
      toast.error('Failed to fetch group members');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep === 4) {
        // Fetch group members when moving from step 4 to 5
        fetchGroupMembers(formData.selectedGroupIds);
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        if (!formData.customerId) {
          toast.error('Please select a customer');
          return false;
        }
        return true;
      case 2:
        if (!formData.name || !formData.startDate || !formData.cloudProvider || formData.minimumServiceFee < 0 || formData.platformCost < 0) {
          toast.error('Please fill in all required fields. Minimum Service Fee and Platform Cost cannot be negative');
          return false;
        }
        if (formData.endDate && formData.endDate < formData.startDate) {
          setDateError('End Date cannot be earlier than Start Date');
          return false;
        }
        const patchingError = validatePatchingServicesForm(patchingServices);
        if (patchingError) {
          toast.error(patchingError);
          return false;
        }
        setDateError('');
        return true;
      case 3: {
        setStep3FieldErrors({});
        if (!formData.cloudProvider) {
          toast.error('Please select a cloud provider first');
          return false;
        }
        const errs: Record<string, string> = {};
        if (formData.cloudProvider === 'Azure') {
          if (!formData.azureDetails.subscriptionId?.trim()) {
            errs['azure.subscriptionId'] = 'Subscription ID is required';
          }
          if (!formData.azureDetails.tenantId?.trim()) {
            errs['azure.tenantId'] = 'Tenant ID is required';
          }
          if (!formData.azureDetails.clientId?.trim()) {
            errs['azure.clientId'] = 'Client ID is required';
          }
          if (!formData.azureDetails.clientSecret?.trim()) {
            errs['azure.clientSecret'] = 'Client Secret is required';
          }
        } else if (formData.cloudProvider === 'AWS') {
          if (!formData.awsDetails.accountId?.trim()) {
            errs['aws.accountId'] = 'Account ID is required';
          }
          if (!formData.awsDetails.region?.trim()) {
            errs['aws.region'] = 'Select a region or choose Select All Regions';
          }
          if (!formData.awsDetails.accessKeyId?.trim()) {
            errs['aws.accessKeyId'] = 'Access Key ID is required';
          }
          if (!formData.awsDetails.secretAccessKey?.trim()) {
            errs['aws.secretAccessKey'] = 'Secret Access Key is required';
          }
        } else if (formData.cloudProvider === 'GCP') {
          if (!formData.gcpDetails.projectId?.trim()) {
            errs['gcp.projectId'] = 'Project ID is required';
          }
          if (!formData.gcpDetails.region?.trim()) {
            errs['gcp.region'] = 'Select a region or choose All regions';
          }
          if (!formData.gcpDetails.serviceAccount?.trim()) {
            errs['gcp.serviceAccount'] = 'Service account key (JSON) is required';
          }
        }
        if (Object.keys(errs).length > 0) {
          setStep3FieldErrors(errs);
          toast.error('Please fix the highlighted fields');
          return false;
        }
        return true;
      }
      case 4:
        if (formData.selectedGroupIds.length === 0) {
          toast.error('Please select at least one group');
          return false;
        }
        return true;
      case 5:
        // Validate agent rates
        const invalidRates = formData.agentRates.some(rate =>
          rate.hourlyRate < 0 || !rate.role
        );
        if (invalidRates) {
          toast.error('Please ensure all agent rates are valid (rate cannot be negative)');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    try {
      setSubmitting(true);

      // Step 1: Create engagement
      const engagementData = {
        name: formData.name,
        cloudProvider: formData.cloudProvider,
        azureDetails: formData.cloudProvider === 'Azure' ? formData.azureDetails : null,
        awsDetails:
          formData.cloudProvider === 'AWS' ? normalizeAwsDetailsForApi(formData.awsDetails) : null,
        gcpDetails:
          formData.cloudProvider === 'GCP' ? normalizeGcpDetailsForApi(formData.gcpDetails) : null,
        description: formData.description || null,
        customerId: formData.customerId,
        billingType: formData.billingType,
        currency: formData.currency,
        startDate: dateToUTCNoon(formData.startDate),
        endDate: formData.endDate ? dateToUTCNoon(formData.endDate) : null,
        totalBudget: formData.totalBudget,
        contractValue: formData.contractValue,
        minimumServiceFee: formData.minimumServiceFee,
        platformCost: formData.platformCost,
        billable: formData.billable,
        isServiceCreditEngagement: formData.isServiceCreditEngagement,
        complimentaryHours: formData.isServiceCreditEngagement ? formData.complimentaryHours : null,
        patchingServices: patchingServicesToApiPayload(patchingServices)
      };

      // Create engagement - data not logged to console for security
      const engagementResponse = await api.post('/engagements', engagementData);
      const engagementId = engagementResponse.data.id;

      // Step 2: Assign groups
      if (formData.selectedGroupIds.length > 0) {
        await api.post(`/engagements/${engagementId}/assign-groups`, {
          groupIds: formData.selectedGroupIds,
          defaultHourlyRate: 100,
          defaultRole: 'DEVELOPER'
        });

        // Step 3: Update individual agent rates
        const rateUpdates = formData.agentRates.map(async (rate) => {
          // Find the agent ID for this user
          const engagementDetail = await api.get(`/engagements/${engagementId}`);
          const agent = engagementDetail.data.EngagementAgent?.find((a: any) => a.userId === rate.userId);
          
          if (agent) {
            return api.put(`/engagements/${engagementId}/agents/${agent.id}/rate`, {
              hourlyRate: rate.hourlyRate,
              role: rate.role,
              allocatedHours: rate.allocatedHours
            });
          }
        });

        await Promise.all(rateUpdates.filter(Boolean));
      }

      toast.success('Engagement created successfully!');
      navigate(`/engagements/${engagementId}`);
    } catch (error: any) {
      // Error creating engagement - do not log to console (may contain sensitive data)
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to create engagement';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const updateFormData = (updates: Partial<EngagementFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const updateAgentRate = (userId: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      agentRates: prev.agentRates.map(rate =>
        rate.userId === userId ? { ...rate, [field]: value } : rate
      )
    }));
  };

  const handleCustomerCreated = (newCustomer: Customer) => {
    setCustomers(prev => [...prev, newCustomer]);
    setFormData(prev => ({ ...prev, customerId: newCustomer.id }));
    toast.success('Customer created successfully!');
  };

  const steps = [
    { number: 1, name: 'Customer', icon: UserGroupIcon },
    { number: 2, name: 'Details', icon: CalendarIcon },
    { number: 3, name: 'Cloud Provider Details', icon: DocumentTextIcon },
    { number: 4, name: 'Groups', icon: UserGroupIcon },
    { number: 5, name: 'Agent Rates', icon: CurrencyDollarIcon },
    { number: 6, name: 'Review', icon: CheckCircleIcon }
  ];

  const renderStepIndicator = () => (
    <div className="bg-white px-6 py-8 shadow-sm border-b border-gray-200">
      <div className="max-w-6xl mx-auto">
        <nav aria-label="Progress">
          <ol className="flex items-center justify-between">
            {steps.map((step, stepIdx) => {
              const isCompleted = step.number < currentStep;
              const isCurrent = step.number === currentStep;
              const isPending = step.number > currentStep;

              return (
                <li key={step.name} className="flex flex-col items-center relative flex-1">
                  <div className="flex items-center">
                    <div className={`
                      relative flex h-12 w-12 items-center justify-center rounded-full transition-all duration-200
                      ${isCompleted ? 'bg-blue-600 shadow-lg' : ''}
                      ${isCurrent ? 'bg-blue-600 shadow-lg ring-4 ring-blue-100' : ''}
                      ${isPending ? 'bg-gray-200 border-2 border-gray-300' : ''}
                    `}>
                      {isCompleted ? (
                        <CheckCircleIcon className="h-6 w-6 text-white" />
                      ) : (
                        <step.icon className={`h-6 w-6 ${
                          isCurrent ? 'text-white' : isPending ? 'text-gray-400' : 'text-white'
                        }`} />
                      )}
                    </div>
                    
                    {stepIdx !== steps.length - 1 && (
                      <div className={`
                        hidden sm:block absolute top-6 left-full w-full h-0.5 transition-all duration-200
                        ${isCompleted ? 'bg-blue-600' : 'bg-gray-300'}
                      `} style={{ zIndex: -1 }} />
                    )}
                  </div>
                  
                  <div className="mt-3 text-center max-w-20">
                    <div className={`text-xs font-medium uppercase tracking-wide ${
                      isCurrent ? 'text-blue-600' : isCompleted ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                      Step {step.number}
                    </div>
                    <div className={`text-sm font-semibold mt-1 leading-tight ${
                      isCurrent ? 'text-gray-900' : isCompleted ? 'text-gray-700' : 'text-gray-500'
                    }`}>
                      {step.name}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900">Select Customer</h3>
        <p className="mt-2 text-base text-gray-600">
          Choose the customer for this engagement
        </p>
      </div>

      <div>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer *
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={formData.customerId}
              onChange={(e) => updateFormData({ customerId: e.target.value })}
              required
            >
              <option value="">Select a customer...</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.accountName} ({customer.accountShortName})
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => setShowCustomerModal(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            title="Create new customer"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>
        {customers.length === 0 && (
          <p className="mt-2 text-sm text-gray-500">
            No customers found. Click the + button to create a new customer.
          </p>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900">Engagement Details</h3>
        <p className="mt-2 text-base text-gray-600">
          Enter the basic information for this engagement
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <FormField
          label="Engagement Name"
          name="name"
          value={formData.name}
          onChange={(e) => updateFormData({ name: e.target.value })}
          required
          placeholder="Enter engagement name"
          className="sm:col-span-2"
        />

        <FormSelect
          label="Cloud Provider"
          name="cloudProvider"
          value={formData.cloudProvider}
          onChange={(e) => updateFormData({ cloudProvider: e.target.value })}
          required
          placeholder="Select Cloud Provider"
          options={[
            { value: 'Azure', label: 'Azure' },
            { value: 'AWS', label: 'AWS' },
            { value: 'GCP', label: 'GCP' }
          ]}
          className="sm:col-span-2"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Billing Type
          </label>
          <select
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            value={formData.billingType}
            onChange={(e) => updateFormData({ billingType: e.target.value })}
          >
            <option value="HOURLY">Hourly</option>
            <option value="FIXED">Fixed Price</option>
            <option value="RETAINER">Retainer</option>
            <option value="MILESTONE">Milestone</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Currency
          </label>
          <select
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            value={formData.currency}
            onChange={(e) => updateFormData({ currency: e.target.value })}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
            <option value="CAD">CAD</option>
          </select>
        </div>

        <FormField
          label="Start Date"
          name="startDate"
          type="date"
          value={formData.startDate}
          onChange={(e) => {
            setDateError('');
            updateFormData({ startDate: e.target.value });
          }}
          required
        />

        <div>
          <FormField
            label="End Date"
            name="endDate"
            type="date"
            value={formData.endDate}
            onChange={(e) => {
              setDateError('');
              updateFormData({ endDate: e.target.value });
            }}
            min={formData.startDate}
          />
          {dateError && (
            <p className="mt-1 text-sm text-red-600">{dateError}</p>
          )}
        </div>

        <FormField
          label="Minimum Service Fee"
          name="minimumServiceFee"
          type="number"
          min="0"
          step="0.01"
          value={formData.minimumServiceFee ?? ''}
          onChange={(e) => updateFormData({ minimumServiceFee: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
          required
          placeholder="Enter minimum service fee"
        />

        <FormField
          label="Platform Cost"
          name="platformCost"
          type="number"
          min="0"
          step="0.01"
          value={formData.platformCost ?? ''}
          onChange={(e) => updateFormData({ platformCost: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
          required
          placeholder="Enter platform cost"
        />

        <div className="sm:col-span-2">
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700">Billable</legend>
            <div className="mt-2 flex items-center gap-6">
              <label className="inline-flex items-center gap-2 text-sm text-gray-900">
                <input
                  type="radio"
                  name="billable"
                  checked={formData.billable}
                  onChange={() => updateFormData({ billable: true })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                Yes
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-900">
                <input
                  type="radio"
                  name="billable"
                  checked={!formData.billable}
                  onChange={() => updateFormData({ billable: false })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                No
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Non-billable engagements count toward &quot;Non Billable Hours&quot; on the bandwidth allocation dashboard.
            </p>
          </fieldset>
        </div>

        <div className="sm:col-span-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="createIsServiceCreditEngagement"
              checked={formData.isServiceCreditEngagement}
              onChange={(e) => {
                const checked = e.target.checked;
                updateFormData({
                  isServiceCreditEngagement: checked,
                  complimentaryHours: checked ? formData.complimentaryHours : null
                });
              }}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="createIsServiceCreditEngagement" className="ml-2 block text-sm text-gray-900">
              Service Credit Engagement
            </label>
          </div>
          {formData.isServiceCreditEngagement && (
            <div className="mt-3 max-w-xs">
              <FormField
                label="Complimentary Hours"
                name="complimentaryHours"
                type="number"
                min="0"
                step="0.01"
                value={formData.complimentaryHours ?? ''}
                onChange={(e) =>
                  updateFormData({
                    complimentaryHours:
                      e.target.value === '' ? null : parseFloat(e.target.value)
                  })
                }
                placeholder="e.g. 40"
              />
            </div>
          )}
        </div>

        <PatchingServicesSection
          value={patchingServices}
          onChange={setPatchingServices}
          currency={formData.currency}
        />

        <FormTextarea
          label="Description"
          name="description"
          value={formData.description}
          onChange={(e) => updateFormData({ description: e.target.value })}
          rows={3}
          placeholder="Enter engagement description"
          className="sm:col-span-2"
        />
      </div>
    </div>
  );

  const renderStep3 = () => {
    if (!formData.cloudProvider) {
      return (
        <div className="space-y-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900">Cloud Provider Details</h3>
            <p className="mt-2 text-base text-gray-600">
              Please select a cloud provider in the previous step first
            </p>
          </div>
        </div>
      );
    }

    const clearStep3FieldError = (errorKey: string) => {
      setStep3FieldErrors((prev) => {
        if (!prev[errorKey]) return prev;
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    };

    const updateCloudDetails = (provider: string, field: string, value: string) => {
      const prefix = provider === 'Azure' ? 'azure' : provider === 'AWS' ? 'aws' : 'gcp';
      clearStep3FieldError(`${prefix}.${field}`);
      if (provider === 'Azure') {
        updateFormData({
          azureDetails: { ...formData.azureDetails, [field]: value }
        });
      } else if (provider === 'AWS') {
        updateFormData({
          awsDetails: { ...formData.awsDetails, [field]: value }
        });
      } else if (provider === 'GCP') {
        updateFormData({
          gcpDetails: { ...formData.gcpDetails, [field]: value }
        });
      }
    };

    const borderClass = (errorKey: string) =>
      `mt-1 block w-full rounded-md border shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
        step3FieldErrors[errorKey] ? 'border-red-500' : 'border-gray-300'
      }`;

    const renderAzureFields = () => (
      <div className="bg-blue-50 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Azure Configuration</h4>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <RequiredFieldLabel required>Subscription ID</RequiredFieldLabel>
            <input
              type="password"
              className={borderClass('azure.subscriptionId')}
              value={formData.azureDetails.subscriptionId}
              onChange={(e) => updateCloudDetails('Azure', 'subscriptionId', e.target.value)}
              placeholder="Enter subscription ID"
              required
            />
            {step3FieldErrors['azure.subscriptionId'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['azure.subscriptionId']}</p>
            )}
          </div>

          <div>
            <RequiredFieldLabel required>Tenant ID</RequiredFieldLabel>
            <input
              type="password"
              className={borderClass('azure.tenantId')}
              value={formData.azureDetails.tenantId}
              onChange={(e) => updateCloudDetails('Azure', 'tenantId', e.target.value)}
              placeholder="Enter tenant ID"
              required
            />
            {step3FieldErrors['azure.tenantId'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['azure.tenantId']}</p>
            )}
          </div>

          <div>
            <RequiredFieldLabel required>Client ID</RequiredFieldLabel>
            <input
              type="password"
              className={borderClass('azure.clientId')}
              value={formData.azureDetails.clientId}
              onChange={(e) => updateCloudDetails('Azure', 'clientId', e.target.value)}
              placeholder="Enter client ID"
              required
            />
            {step3FieldErrors['azure.clientId'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['azure.clientId']}</p>
            )}
          </div>

          <div>
            <RequiredFieldLabel required>Client Secret</RequiredFieldLabel>
            <input
              type="password"
              className={borderClass('azure.clientSecret')}
              value={formData.azureDetails.clientSecret}
              onChange={(e) => updateCloudDetails('Azure', 'clientSecret', e.target.value)}
              placeholder="Enter client secret"
              required
            />
            {step3FieldErrors['azure.clientSecret'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['azure.clientSecret']}</p>
            )}
          </div>

          <div className="sm:col-span-2">
            <RequiredFieldLabel>Authority URL</RequiredFieldLabel>
            <input
              type="text"
              className={borderClass('azure.authorityUrl')}
              value={formData.azureDetails.authorityUrl}
              onChange={(e) => updateCloudDetails('Azure', 'authorityUrl', e.target.value)}
              placeholder="e.g., https://login.microsoftonline.com/your-tenant-id"
            />
            {step3FieldErrors['azure.authorityUrl'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['azure.authorityUrl']}</p>
            )}
          </div>
        </div>
      </div>
    );

    const renderAWSFields = () => (
      <div className="bg-orange-50 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-gray-900 mb-4">AWS Configuration</h4>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <RequiredFieldLabel required>Account ID</RequiredFieldLabel>
            <input
              type="password"
              className={borderClass('aws.accountId')}
              value={formData.awsDetails.accountId}
              onChange={(e) => updateCloudDetails('AWS', 'accountId', e.target.value)}
              placeholder="Enter AWS account ID"
              required
            />
            {step3FieldErrors['aws.accountId'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['aws.accountId']}</p>
            )}
          </div>

          <div>
            <RequiredFieldLabel required>Region</RequiredFieldLabel>
            <select
              className={borderClass('aws.region')}
              value={formData.awsDetails.region}
              onChange={(e) => updateCloudDetails('AWS', 'region', e.target.value)}
              required
            >
              <option value="">Select region</option>
              <option value={AWS_SELECT_ALL_REGIONS_VALUE}>Select All Regions</option>
              {AWS_REGION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {step3FieldErrors['aws.region'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['aws.region']}</p>
            )}
            {formData.awsDetails.region === AWS_SELECT_ALL_REGIONS_VALUE && (
              <p className="mt-1 text-xs text-gray-600">
                All {AWS_ALL_REGION_CODES.length} supported commercial regions will be stored for this engagement.
              </p>
            )}
          </div>

          <div>
            <RequiredFieldLabel required>Access Key ID</RequiredFieldLabel>
            <input
              type="password"
              className={borderClass('aws.accessKeyId')}
              value={formData.awsDetails.accessKeyId}
              onChange={(e) => updateCloudDetails('AWS', 'accessKeyId', e.target.value)}
              placeholder="Enter access key ID"
              required
            />
            {step3FieldErrors['aws.accessKeyId'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['aws.accessKeyId']}</p>
            )}
          </div>

          <div>
            <RequiredFieldLabel required>Secret Access Key</RequiredFieldLabel>
            <input
              type="password"
              className={borderClass('aws.secretAccessKey')}
              value={formData.awsDetails.secretAccessKey}
              onChange={(e) => updateCloudDetails('AWS', 'secretAccessKey', e.target.value)}
              placeholder="Enter secret access key"
              required
            />
            {step3FieldErrors['aws.secretAccessKey'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['aws.secretAccessKey']}</p>
            )}
          </div>

          <div>
            <RequiredFieldLabel optionalHint="optional">VPC ID</RequiredFieldLabel>
            <input
              type="text"
              className={borderClass('aws.vpcId')}
              value={formData.awsDetails.vpcId}
              onChange={(e) => updateCloudDetails('AWS', 'vpcId', e.target.value)}
              placeholder="e.g., vpc-12345678 (optional)"
            />
            {step3FieldErrors['aws.vpcId'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['aws.vpcId']}</p>
            )}
          </div>

          <div>
            <RequiredFieldLabel optionalHint="optional">IAM Role</RequiredFieldLabel>
            <input
              type="text"
              className={borderClass('aws.iamRole')}
              value={formData.awsDetails.iamRole}
              onChange={(e) => updateCloudDetails('AWS', 'iamRole', e.target.value)}
              placeholder="e.g., role ARN (optional)"
            />
            {step3FieldErrors['aws.iamRole'] && (
              <p className="mt-1 text-sm text-red-600">{step3FieldErrors['aws.iamRole']}</p>
            )}
          </div>
        </div>
      </div>
    );

    const renderGCPFields = () => (
      <div className="bg-green-50 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-gray-900 mb-4">GCP Configuration</h4>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <RequiredFieldLabel required>Project ID</RequiredFieldLabel>
          <input
            type="text"
            className={borderClass('gcp.projectId')}
            value={formData.gcpDetails.projectId}
            onChange={(e) => updateCloudDetails('GCP', 'projectId', e.target.value)}
            placeholder="e.g., my-gcp-project-123456"
            required
          />
          {step3FieldErrors['gcp.projectId'] && (
            <p className="mt-1 text-sm text-red-600">{step3FieldErrors['gcp.projectId']}</p>
          )}
        </div>

        <div>
          <RequiredFieldLabel required>Region</RequiredFieldLabel>
          <select
            className={borderClass('gcp.region')}
            value={formData.gcpDetails.region}
            onChange={(e) => updateCloudDetails('GCP', 'region', e.target.value)}
            required
          >
            <option value="">Select region</option>
            <option value={GCP_SELECT_ALL_REGIONS_VALUE}>All regions (standard commercial list)</option>
            {GCP_REGION_OPTIONS.map((opt: GcpRegionOption) => (
              <option key={opt.value} value={opt.value}>
                {opt.label} ({opt.value})
              </option>
            ))}
          </select>
          {step3FieldErrors['gcp.region'] && (
            <p className="mt-1 text-sm text-red-600">{step3FieldErrors['gcp.region']}</p>
          )}
        </div>

        <div>
          <RequiredFieldLabel>Zone</RequiredFieldLabel>
          <input
            type="text"
            className={borderClass('gcp.zone')}
            value={formData.gcpDetails.zone}
            onChange={(e) => updateCloudDetails('GCP', 'zone', e.target.value)}
            placeholder="e.g., us-central1-a"
          />
        </div>

        <div className="sm:col-span-2">
          <RequiredFieldLabel required>Service account key (JSON)</RequiredFieldLabel>
          <textarea
            rows={4}
            className={borderClass('gcp.serviceAccount')}
            value={formData.gcpDetails.serviceAccount}
            onChange={(e) => updateCloudDetails('GCP', 'serviceAccount', e.target.value)}
            placeholder="Paste the entire contents of the .json key from GCP (IAM → Service account → Keys). The email alone is not enough."
            required
          />
          {step3FieldErrors['gcp.serviceAccount'] && (
            <p className="mt-1 text-sm text-red-600">{step3FieldErrors['gcp.serviceAccount']}</p>
          )}
        </div>
        </div>
      </div>
    );

    return (
      <div className="space-y-8">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900">Cloud provider credentials</h3>
          <p className="mt-2 text-base text-gray-600">
            Enter the connection details for {formData.cloudProvider}. Required fields are marked with an asterisk.
          </p>
        </div>

        {formData.cloudProvider === 'Azure' && renderAzureFields()}
        {formData.cloudProvider === 'AWS' && renderAWSFields()}
        {formData.cloudProvider === 'GCP' && renderGCPFields()}
      </div>
    );
  };

  const renderStep4 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900">Select Groups</h3>
        <p className="mt-2 text-base text-gray-600">
          Choose the groups whose members will be assigned as agents to this engagement
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {groups.map(group => (
          <div key={group.id} className="relative">
            <label className="flex items-center space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={formData.selectedGroupIds.includes(group.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    updateFormData({ selectedGroupIds: [...formData.selectedGroupIds, group.id] });
                  } else {
                    updateFormData({ 
                      selectedGroupIds: formData.selectedGroupIds.filter(id => id !== group.id) 
                    });
                  }
                }}
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">{group.name}</div>
                <div className="text-sm text-gray-500">{group.description}</div>
                <div className="text-xs text-gray-400">{group.memberCount} members</div>
              </div>
            </label>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900">Configure Agent Rates</h3>
        <p className="mt-2 text-base text-gray-600">
          Set the hourly rates and roles for each agent from the selected groups
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hourly Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Allocated Hours
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {groupMembers.map(member => {
                const agentRate = formData.agentRates.find(rate => rate.userId === member.userId);
                return (
                  <tr key={member.userId}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {member.user.firstName} {member.user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{member.user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        value={agentRate?.role || 'DEVELOPER'}
                        onChange={(e) => updateAgentRate(member.userId, 'role', e.target.value)}
                      >
                        <option value="DEVELOPER">Developer</option>
                        <option value="SENIOR_DEVELOPER">Senior Developer</option>
                        <option value="ARCHITECT">Architect</option>
                        <option value="PROJECT_MANAGER">Project Manager</option>
                        <option value="BUSINESS_ANALYST">Business Analyst</option>
                        <option value="QA_ENGINEER">QA Engineer</option>
                        <option value="DEVOPS_ENGINEER">DevOps Engineer</option>
                        <option value="CONSULTANT">Consultant</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        value={agentRate?.hourlyRate || 100}
                        onChange={(e) => updateAgentRate(member.userId, 'hourlyRate', parseFloat(e.target.value) || 0)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        value={agentRate?.allocatedHours || ''}
                        onChange={(e) => updateAgentRate(member.userId, 'allocatedHours', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="Optional"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderStep6 = () => {
    const selectedCustomer = customers.find(c => c.id === formData.customerId);
    const selectedGroups = groups.filter(g => formData.selectedGroupIds.includes(g.id));
    const totalProjectedCost = formData.agentRates.reduce((sum, rate) => 
      sum + (rate.allocatedHours || 0) * rate.hourlyRate, 0
    );

    return (
      <div className="space-y-8">
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900">Review & Create</h3>
          <p className="mt-2 text-base text-gray-600">
            Review all the details before creating the engagement
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          <div>
            <h4 className="font-medium text-gray-900">Customer</h4>
            <p className="text-sm text-gray-600">{selectedCustomer?.accountName}</p>
          </div>

          <div>
            <h4 className="font-medium text-gray-900">Engagement Details</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Name:</span> {formData.name}</p>
              <p><span className="font-medium">Billing Type:</span> {formData.billingType}</p>
              <p><span className="font-medium">Start Date:</span> {formData.startDate}</p>
              {formData.endDate && <p><span className="font-medium">End Date:</span> {formData.endDate}</p>}
              {formData.totalBudget && (
                <p><span className="font-medium">Budget:</span> {formData.currency} {formData.totalBudget.toLocaleString()}</p>
              )}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900">Selected Groups</h4>
            <div className="text-sm text-gray-600">
              {selectedGroups.map(group => (
                <p key={group.id}>{group.name} ({group.memberCount} members)</p>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900">Agent Summary</h4>
            <div className="text-sm text-gray-600">
              <p>{formData.agentRates.length} agents assigned</p>
              <p>Projected cost: {formData.currency} {totalProjectedCost.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Permission check handled by useEffect redirect
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to create engagements.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Create New Engagement</h1>
            <p className="mt-3 text-lg text-gray-600">
              Set up a new customer engagement with resource allocation and billing
            </p>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          <div className="px-8 py-10">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
            {currentStep === 6 && renderStep6()}
          </div>

          {/* Button Section */}
          <div className="px-8 py-6 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 flex justify-between items-center">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <ChevronLeftIcon className="mr-2 h-4 w-4" />
              Previous
            </button>

            <div className="text-sm text-gray-500 font-medium">
              Step {currentStep} of {steps.length}
            </div>

            {currentStep < 6 ? (
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
              >
                Next
                <ChevronRightIcon className="ml-2 h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center px-8 py-3 border border-transparent shadow-lg text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin -ml-1 mr-3 h-4 w-4 text-white">
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    </div>
                    Creating...
                  </>
                ) : (
                  'Create Engagement'
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <CustomerCreateModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCustomerCreated={handleCustomerCreated}
      />
    </div>
  );
};

export default EngagementCreate;

